import {
  Award,
  Banknote,
  CircleUser,
  Compass,
  FileText,
  IdCard,
  ListChecks,
  Palmtree,
  Percent,
  ReceiptText,
} from 'lucide-react';

import type { Guide } from '../types';

/**
 * My Space (self-service cabinet) guides. Plain-language how-to + the "why".
 * A few sidebar sections are combined where they read better together
 * (pay = salary + payslips + bank accounts; growth = reviews + plan + skills).
 */
export const MY_SPACE_GUIDES: Guide[] = [
  {
    slug: 'my-space-overview',
    title: 'Welcome to My Space',
    module: 'my-space',
    summary: 'What My Space is and how to find your way around.',
    icon: Compass,
    body: `**My Space** is your personal corner of the workspace. Everything here is
*yours* — only you (and the people whose job it is, like HR or Finance) can see it.

### Why it exists
Instead of emailing HR for your payslip or hunting for a document, you find what
you need yourself, in one place, whenever you need it.

### What you'll find here
- **Account & Employee Record** — who you are in the system.
- **Getting paid** — your salary, invoices, payslips and payout accounts.
- **Time Off** — request and track leave.
- **Growth** — reviews, your development plan and skills.
- **Onboarding** — your getting-started checklist.
- **Documents** — files shared with you (contracts, agreements…).

### Good to know
- You only ever see your **own** information here.
- Some figures (like salary) are visible only to you and the relevant teams.
- Use the left sidebar to jump between sections.`,
  },
  {
    slug: 'my-space-account',
    title: 'Account',
    module: 'my-space',
    summary: 'Your login, profile photo, contact details and preferences.',
    icon: CircleUser,
    body: `Your **Account** is your sign-in identity — your name, username, email and
role, plus your security settings.

### What you'll see
- Your **name, username, email and role** (these are managed for you — read-only here).
- A **Change password** form — set a strong one you don't reuse elsewhere.
- **Manage security** — add 2-factor authentication for an extra layer of protection.

### Elsewhere
- Your **profile photo** is set on the **Employee Record** page.
- Switch **light / dark / system** theme from the toggle in the top-right of the app.
- **Online status:** the dot on your avatar shows how colleagues see you — green while you're
  online. Choose **Appear offline** in the user menu (top-right) to hide it; the eye there is
  crossed out while you appear offline. Staff see a green dot on people who are online on the
  Employees pages, and "Last seen …" on hover.

### Why it's here
One safe place to keep your credentials current — change your password or turn on
2FA yourself, without raising a ticket.`,
  },
  {
    slug: 'my-space-employee-record',
    title: 'Employee Record',
    module: 'my-space',
    summary: 'The HR view of you: role, department, key dates.',
    icon: IdCard,
    body: `Your **Employee Record** is your HR profile — and where you manage your
**profile photo**.

### What you can do
- **Change your photo** (the avatar shown across the app).
- Edit your **personal info** (your own contact details).

### What HR maintains (read-only)
The **employment record** — job title, department, employment type, manager,
hire date and trial-period end — is managed by HR.

### Why it's here
It's the single source of truth HR, Finance and Payroll rely on, so your pay,
leave and reporting line all line up.

> Something in the employment record looks wrong? Those fields are HR-managed —
> reach out to them to get it corrected.`,
  },
  {
    slug: 'my-space-getting-paid',
    title: 'Getting paid',
    module: 'my-space',
    summary: 'How salary, invoices, payslips and bank accounts fit together.',
    icon: Banknote,
    body: `This is the money side of My Space. Three pieces work together:

### 1. My Salary
Your salary history, recorded by HR/Finance. It's the basis for what you're paid
and is visible only to you and the relevant teams — a read-only record here.

### 2. Invoices & Payslips
Everyone is paid **by invoice**. Each pay period:
1. Your salary is turned into an hourly rate and a **staff invoice** is prepared.
2. You **review and sign** it (see the *Invoices* guide).
3. Once signed and paid, it becomes a **payslip** you can download any time.

### 3. Bank Accounts
Where the money goes. You can **see the payout accounts on file** here, but
Finance/HR maintain them — to add or change an account, contact them (changing a
payout account mid-cycle could disrupt a pay run).

### Why it's here
It keeps your earnings transparent: you can always see what you're owed, sign off
on it, download your payslips, and check the account you'll be paid into.`,
  },
  {
    slug: 'my-space-invoices',
    title: 'Invoices',
    module: 'my-space',
    summary: 'Review, sign and download the invoices you raise to get paid.',
    icon: ReceiptText,
    body: `Your **Invoices** are the documents that get you paid — prepared from your
salary each period, then reviewed and signed by you.

### The lifecycle
- **Draft** — being prepared by Finance; amounts can still change.
- **Issued** — ready for you to review and sign.
- **Signed** — you've confirmed the hours and amount are correct.
- **Paid** — payment has been made.

### How to use it
1. Open an issued invoice and check the period, hours and total.
2. Click **View PDF** to read the full document, or **Download PDF** to keep a copy.
3. If everything's right, **Sign** it.

### Why it's here
Signing is your confirmation that the figures are correct — it protects both you
and the company, and creates a clean, auditable record.`,
  },
  {
    slug: 'my-space-commissions',
    title: 'Commissions',
    module: 'my-space',
    summary: 'Track the commission you’ve earned on closed deals.',
    icon: Percent,
    roles: ['sales_manager'],
    body: `If you work in sales, **Commissions** shows what you've earned on the deals
you own.

### How it works
- Each deal has a **commission %** (your default rate seeds new deals).
- While a deal is in progress you'll see the **projected** commission; once the
  client **pays**, it becomes the **actual** earned amount.
- Filter by period to reconcile against a pay run. (Read-only — pay-out is a
  Finance action.)

### Why it's here
A transparent, real-time view of your earnings — no spreadsheets, no guessing
when a deal will pay out.`,
  },
  {
    slug: 'my-space-time-off',
    title: 'Time Off',
    module: 'my-space',
    summary: 'Request leave and track its status and your balance.',
    icon: Palmtree,
    body: `**Time Off** is where you request and track leave.

### How to use it
1. Click **Request time off**, pick the dates and type, add an optional reason.
2. **Submit** — it goes to your approver as **pending**.
3. Track the status (**pending → approved / declined**) right here. While it's
   still pending you can **edit** or **cancel** the request.

### Why it's here
One clear place for requests and history means no lost emails and a shared,
accurate record for you and your manager.`,
  },
  {
    slug: 'my-space-growth',
    title: 'Growth',
    module: 'my-space',
    summary: 'Performance reviews, your development plan and skills.',
    icon: Award,
    body: `**Growth** brings together how you're doing and where you're heading.

### Performance Reviews
Read reviews shared with you, **acknowledge** them, and tick off any agreed
action items.

### Development Plan (PDP)
Your goals over time. Move your own goals along (**open → in progress → done**)
as you make progress.

### Skills
The skills you hold and their levels. **Add a skill** (pick one from the shared
catalogue or type a new one), set its **level (1–5)**, and adjust the level inline
as you grow — so your profile reflects what you can do.

### Why it's here
Career growth shouldn't live in a forgotten doc. Keeping reviews, goals and skills
together makes development an ongoing, visible conversation.`,
  },
  {
    slug: 'my-space-onboarding',
    title: 'Onboarding',
    module: 'my-space',
    summary: 'Your getting-started checklist for the first days.',
    icon: ListChecks,
    body: `**Onboarding** is your guided checklist for getting set up.

### How to use it
1. Open your checklist and hit **Start** when you begin.
2. Work through each item and **tick it off** as you go.
3. Watch your progress fill in — you'll know exactly what's left.

### Why it's here
A clear, shared checklist means nothing important slips through the cracks in
your first weeks, and HR can see how you're getting on.`,
  },
  {
    slug: 'my-space-documents',
    title: 'Documents',
    module: 'my-space',
    summary: 'Files shared with you — contracts, agreements and more.',
    icon: FileText,
    body: `**Documents** holds the files linked to you — things like your contract,
NDA or other agreements.

### How to use it
- Browse the documents shared with you.
- **Preview** a file inline, or **download** it for your records.

### Why it's here
Your important paperwork in one secure place — always available, never lost in an
inbox. You only ever see documents that are yours.`,
  },
];
